/**
 * ledger — Hoàn tác (đảo) trả thẻ, chuyển khoản, trả nợ: MỘT transaction cho mỗi lần đảo, có bút toán đảo (audit),
 * và mở lại khoản dự kiến đã sinh ra lần trả đó. Chính sách ví thống nhất với sổ cái (checkChange).
 *
 * Truy vấn (tìm khoản dự kiến/khoản chi phí liên kết/lần trả mới nhất) chạy NGOÀI transaction vì Firestore client
 * không cho query trong transaction; trước khi ghi, transaction đọc lại và xác minh liên kết còn khớp.
 */
import { getFirestore, collection, doc, getDocs, query, where, orderBy, limit, runTransaction, Timestamp } from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { checkChange, type SourceState } from './effects';
import { isQuotaError, QUOTA_MESSAGE } from './errors';
import { LedgerError, type LedgerResult } from './ledger';
import { cardUsedAfterLoanReversal } from '../finance/debt';

type Data = Record<string, any>;
const base = (uid: string) => ['artifacts', APP_ID, 'users', uid] as const;
const money = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;

const guard = async (fn: () => Promise<Partial<LedgerResult>>): Promise<LedgerResult> => {
  try { return { success: true, ...(await fn()) }; }
  catch (e) {
    if (e instanceof LedgerError) return { success: false, error: e.message };
    if (isQuotaError(e)) return { success: false, error: QUOTA_MESSAGE };
    console.error('Reversal error:', e);
    return { success: false, error: `Không đảo được (chưa có gì thay đổi): ${(e as Error).message}` };
  }
};

/** Khoản dự kiến trả thẻ/nợ đang trỏ tới lần trả này (nếu có). */
const findPlan = async (userId: string, paymentId: string): Promise<string | null> => {
  const db = getFirestore(); const [a, b, c, d] = base(userId);
  const snap = await getDocs(query(collection(db, a, b, c, d, 'plannedPayments'), where('actualPaymentId', '==', paymentId)));
  return snap.docs[0]?.id ?? null;
};

const reopenPatch = (now: Timestamp): Data => ({ status: 'pending', actualPaymentId: null, confirmedAt: null, confirmedBy: null, reopenedAt: now, updatedAt: now });

const audit = (db: any, userId: string, tx: any, now: Timestamp, row: Data) => {
  const [a, b, c, d] = base(userId);
  tx.set(doc(collection(db, a, b, c, d, 'transactions')), { ...row, timestamp: now, createdAt: now });
};

// ───────── Trả thẻ tín dụng ─────────
export const reverseCardPayment = (userId: string, paymentId: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore(); const [a, b, c, d] = base(userId);
    const planId = await findPlan(userId, paymentId);
    const warnings: string[] = [];
    let reopened = 0;
    await runTransaction(db, async (tx) => {
      warnings.length = 0; reopened = 0;
      const now = Timestamp.now();
      const payRef = doc(db, a, b, c, d, 'creditPayments', paymentId);
      const paySnap = await tx.get(payRef);
      if (!paySnap.exists()) throw new LedgerError('Không tìm thấy lần trả thẻ — có thể đã được hoàn tác.');
      const p = paySnap.data() as Data;
      const amount = Number(p.amount) || 0;
      const cardRef = doc(db, a, b, c, d, 'sources', p.creditCardId);
      const walletRef = doc(db, a, b, c, d, 'sources', p.sourceWalletId);
      const planRef = planId ? doc(db, a, b, c, d, 'plannedPayments', planId) : null;
      const [cardSnap, walletSnap, planSnap] = await Promise.all([tx.get(cardRef), tx.get(walletRef), planRef ? tx.get(planRef) : Promise.resolve(null)]);

      tx.delete(payRef);
      if (cardSnap.exists()) {
        const card = cardSnap.data() as Data;
        const restored = (Number(card.usedCredit) || 0) + amount;
        // Luật Firestore không cho dư nợ vượt hạn mức: báo rõ thay vì để ghi bị từ chối
        if (Number(card.creditLimit) > 0 && restored > Number(card.creditLimit)) throw new LedgerError(`Không đảo được: dư nợ thẻ "${card.name ?? ''}" sẽ thành ${money(restored)}, vượt hạn mức ${money(Number(card.creditLimit))}. Hãy nâng hạn mức hoặc ghi nhận thêm khoản trả khác trước.`);
        tx.update(cardRef, { usedCredit: restored, updatedAt: now });
      }
      else warnings.push('Thẻ gốc đã bị xoá nên không có dư nợ nào để cập nhật.');
      if (walletSnap.exists()) tx.update(walletRef, { balance: (Number((walletSnap.data() as Data).balance) || 0) + amount, updatedAt: now });
      else warnings.push('Ví đã trả đã bị xoá nên không có số dư nào để hoàn lại.');
      if (planRef && planSnap?.exists() && (planSnap.data() as Data).status === 'confirmed' && (planSnap.data() as Data).actualPaymentId === paymentId) {
        tx.update(planRef, reopenPatch(now)); reopened++;
      }
      audit(db, userId, tx, now, { sourceId: p.sourceWalletId, amount, type: 'payment-reversal', relatedId: paymentId, description: `Hoàn tác trả thẻ ${p.creditCardName ?? ''}: hoàn ${money(amount)} vào ${p.sourceWalletName ?? 'ví'}, dư nợ thẻ tăng lại ${money(amount)}` });
    });
    return { warnings, reopened };
  });

// ───────── Chuyển khoản ─────────
export const reverseTransfer = (userId: string, transferId: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore(); const [a, b, c, d] = base(userId);
    // Phí chuyển khoản sinh ra một khoản chi tự động (chỉ là bút toán kế toán, ví đã trừ trong tổng) — đảo thì xoá khoản đó, KHÔNG hoàn tiền lần hai
    const feeSnap = await getDocs(query(collection(db, a, b, c, d, 'expenses'), where('relatedTransferId', '==', transferId)));
    const feeIds = feeSnap.docs.map(x => x.id);
    const warnings: string[] = [];
    await runTransaction(db, async (tx) => {
      warnings.length = 0;
      const now = Timestamp.now();
      const tRef = doc(db, a, b, c, d, 'transfers', transferId);
      const tSnap = await tx.get(tRef);
      if (!tSnap.exists() || (tSnap.data() as Data).status === 'cancelled') throw new LedgerError('Không tìm thấy khoản chuyển — có thể đã được hoàn tác.');
      const t = tSnap.data() as Data;
      const amount = Number(t.amount) || 0, fee = Number(t.fee) || 0;
      const fromRef = doc(db, a, b, c, d, 'sources', t.fromSourceId);
      const toRef = doc(db, a, b, c, d, 'sources', t.toSourceId);
      const [fromSnap, toSnap] = await Promise.all([tx.get(fromRef), tx.get(toRef)]);

      // Ví nhận phải còn đủ tiền để rút lại số đã nhận (tiền mặt được âm kèm cảnh báo)
      if (toSnap.exists()) {
        const pol = checkChange(toSnap.data() as SourceState, 'balance', -amount);
        if (!pol.ok) throw new LedgerError(`Không đảo được: ví nhận "${(toSnap.data() as Data).name ?? ''}" đã dùng bớt số tiền chuyển đến (${pol.error}) Hãy nạp lại ví đó trước.`);
        if (pol.warning) warnings.push(pol.warning);
        tx.update(toRef, { balance: pol.next, updatedAt: now });
      } else warnings.push('Ví nhận đã bị xoá nên không có số dư nào để rút lại.');
      if (fromSnap.exists()) tx.update(fromRef, { balance: (Number((fromSnap.data() as Data).balance) || 0) + amount + fee, updatedAt: now });
      else warnings.push('Ví gửi đã bị xoá nên không có số dư nào để hoàn lại.');

      tx.delete(tRef);
      for (const id of feeIds) tx.delete(doc(db, a, b, c, d, 'expenses', id));
      audit(db, userId, tx, now, { sourceId: t.fromSourceId, amount: amount + fee, type: 'transfer-reversal', relatedId: transferId, description: `Hoàn tác chuyển khoản ${money(amount)}${fee > 0 ? ` (phí ${money(fee)})` : ''}: hoàn ${money(amount + fee)} cho ví gửi, rút ${money(amount)} khỏi ví nhận` });
    });
    return { warnings };
  });

// ───────── Trả nợ vay (chỉ lần trả MỚI NHẤT của khoản vay đó) ─────────
export const reverseDebtPayment = (userId: string, debtId: string, paymentId: string): Promise<LedgerResult> =>
  guard(async () => {
    const db = getFirestore(); const [a, b, c, d] = base(userId);
    const payCol = collection(db, a, b, c, d, 'debts', debtId, 'payments');
    const latest = await getDocs(query(payCol, orderBy('paymentDate', 'desc'), limit(1)));
    if (latest.empty) throw new LedgerError('Không có lịch sử trả nợ.');
    if (latest.docs[0].id !== paymentId) throw new LedgerError('Chỉ hoàn tác được lần trả MỚI NHẤT của khoản vay này — hoàn tác theo thứ tự để không làm sai các kỳ sau.');
    const planId = await findPlan(userId, paymentId);
    const interestSnap = await getDocs(query(collection(db, a, b, c, d, 'expenses'), where('relatedPaymentId', '==', paymentId)));
    const interestIds = interestSnap.docs.map(x => x.id);
    const warnings: string[] = [];
    let reopened = 0;
    await runTransaction(db, async (tx) => {
      warnings.length = 0; reopened = 0;
      const now = Timestamp.now();
      const payRef = doc(db, a, b, c, d, 'debts', debtId, 'payments', paymentId);
      const debtRef = doc(db, a, b, c, d, 'debts', debtId);
      const [paySnap, debtSnap] = await Promise.all([tx.get(payRef), tx.get(debtRef)]);
      if (!paySnap.exists()) throw new LedgerError('Lần trả nợ không còn tồn tại — có thể đã được hoàn tác.');
      if (!debtSnap.exists()) throw new LedgerError('Khoản vay không tồn tại.');
      const p = paySnap.data() as Data, debt = debtSnap.data() as Data;
      const total = Number(p.totalAmount ?? p.amount) || 0;
      const walletRef = p.sourceId ? doc(db, a, b, c, d, 'sources', p.sourceId) : null;
      const planRef = planId ? doc(db, a, b, c, d, 'plannedPayments', planId) : null;
      const cardRef = debt.creditCardId ? doc(db, a, b, c, d, 'sources', String(debt.creditCardId)) : null;
      const [walletSnap, planSnap, cardSnap] = await Promise.all([walletRef ? tx.get(walletRef) : Promise.resolve(null), planRef ? tx.get(planRef) : Promise.resolve(null), cardRef ? tx.get(cardRef) : Promise.resolve(null)]);

      if (walletRef) {
        if (walletSnap?.exists()) tx.update(walletRef, { balance: (Number((walletSnap.data() as Data).balance) || 0) + total, updatedAt: now });
        else warnings.push('Ví đã trả đã bị xoá nên không có số dư nào để hoàn lại.');
      }
      const restored = Math.min((Number(debt.remainingBalance) || 0) + (Number(p.principal) || 0), Number(debt.principal) || Infinity);
      tx.update(debtRef, { remainingBalance: restored, status: restored > 0 ? 'active' : debt.status, updatedAt: now });
      // Khoản vay gắn thẻ: trả gốc đã giảm dư nợ thẻ, hoàn tác thì tăng lại (không vượt hạn mức)
      if (cardRef && cardSnap?.exists() && (Number(p.principal) || 0) > 0) {
        const card = cardSnap.data() as Data;
        tx.update(cardRef, { usedCredit: cardUsedAfterLoanReversal(Number(card.usedCredit) || 0, Number(p.principal) || 0, Number(card.creditLimit) || 0), updatedAt: now });
      }
      tx.delete(payRef);
      for (const id of interestIds) tx.delete(doc(db, a, b, c, d, 'expenses', id));
      if (planRef && planSnap?.exists() && (planSnap.data() as Data).status === 'confirmed' && (planSnap.data() as Data).actualPaymentId === paymentId) { tx.update(planRef, reopenPatch(now)); reopened++; }
      audit(db, userId, tx, now, { debtId, sourceId: p.sourceId || null, amount: total, type: 'payment-reversal', relatedId: paymentId, description: `Hoàn tác trả nợ ${debt.lender ?? ''}: hoàn ${money(total)} (gốc ${money(Number(p.principal) || 0)} + lãi ${money(Number(p.interest) || 0)})` });
    });
    return { warnings, reopened };
  });
